import { userDisplayName } from "@common-models/user";
import { useAppSelector, useAppDispatch } from "@common-reducers/hooks/store.hook";
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import { activeSubscriptionSelector } from "@common-reducers/UserSelectors";
import { AnalyticsService } from "@common-services/analytics-service";
import { getRuntimeUrl } from "@common-services/storage";
import { modifyPlanName, openPaymentSite } from "@common-services/utils";
import { selectCurrentUser } from "@common/reducers/UserSelectors";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { useEffect, useRef } from "react";
import classes from "./status-box.module.scss";

enum PlanNames { free, scheduler, manager, campaigner }
enum FeatureNames { messages, tasks, campaigns }

interface ModalDataPlan {
    name: string;
    statuses: {
        [FeatureNames.messages]: string;
        [FeatureNames.tasks]: string;
        [FeatureNames.campaigns]: string;
    },
    image: string;
    buttonText: string;
}

export type ModalDataPlans = {
    [key in PlanNames]: ModalDataPlan
}

const modalDataPlans: ModalDataPlans = {
    [PlanNames.free]: {
        name: "Free plan",
        statuses: {
            [FeatureNames.messages]: "Up to 3 at a time",
            [FeatureNames.tasks]: "Up to 10 per contact",
            [FeatureNames.campaigns]: "Unlimited, with branding",
        },
        image: "/img/locked-free-plan.svg",
        buttonText: "Unlock all features",
    },
    [PlanNames.scheduler]: {

        name: "Basic plan",
        statuses: {
            [FeatureNames.messages]: "Unlimited messages!",
            [FeatureNames.tasks]: "Up to 10 per contact",
            [FeatureNames.campaigns]: "Unlimited, with branding",
        },
        image: "/img/locked-premium-plan.svg",
        buttonText: "Upgrade now",
    },
    [PlanNames.manager]: {
        name: "Standard plan",
        statuses: {
            [FeatureNames.messages]: "Unlimited messages!",
            [FeatureNames.tasks]: "Unlimited tasks!",
            [FeatureNames.campaigns]: "Unlimited, with branding",
        },
        image: "/img/locked-premium-plan.svg",
        buttonText: "Upgrade now",
    },
    [PlanNames.campaigner]: {
        name: "Pro plan",
        statuses: {
            [FeatureNames.messages]: "Unlimited messages!",
            [FeatureNames.tasks]: "Unlimited tasks!",
            [FeatureNames.campaigns]: "Unlimited without branding!",
        },
        image: "/img/locked-premium-plan.svg",
        buttonText: "Upgrade now",
    },

}

export interface StatusBoxParams {
    onClose?: () => void;
    subscriptionOverride?: any;
    displayNameOverride?: string;
}

export const StatusBox = ({
    onClose,
    subscriptionOverride,
    displayNameOverride
}: StatusBoxParams) => {
    const analyticsService: AnalyticsService = useAnalyticsService();

    const dispatch = useAppDispatch();
    const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
    const displayName = useAppSelector((state) => userDisplayName(selectCurrentUser(state)));
    const activeSubscription = useAppSelector(activeSubscriptionSelector);

    const isSubscriptionLoaded = useAppSelector((state) => state.DBSubscriptionReducer.itemsLoaded);


    const effectiveSubscription = subscriptionOverride !== undefined ? subscriptionOverride : activeSubscription;
    const effectiveDisplayName = displayNameOverride !== undefined ? displayNameOverride : displayName;

    let currentPlan = PlanNames.free;
    switch (effectiveSubscription?.planType) {
        case 'Basic': {
            currentPlan = PlanNames.scheduler;
            break;
        }
        case 'Standard': {
            currentPlan = PlanNames.manager;
            break;
        }
        case 'Pro': {
            currentPlan = PlanNames.campaigner;
            break;
        }
    }

    // Branding text follows the Chargebee remove-branding entitlement, not the plan name (#126).
    const removesBranding = !!effectiveSubscription?.entitlementList?.some((entitlement: { feature_id: string }) => entitlement.feature_id === 'remove-branding');
    const campaignsStatus = currentPlan === PlanNames.free
        ? modalDataPlans[currentPlan].statuses[FeatureNames.campaigns]
        : (removesBranding ? "Unlimited without branding!" : "Unlimited, with branding");

    const modalData = {
        nameOrEmail: effectiveDisplayName,
        planName: modalDataPlans[currentPlan].name,
        statuses: {
            [FeatureNames.messages]: modalDataPlans[currentPlan].statuses[FeatureNames.messages],
            [FeatureNames.tasks]: modalDataPlans[currentPlan].statuses[FeatureNames.tasks],
            [FeatureNames.campaigns]: campaignsStatus,
        },
        image: modalDataPlans[currentPlan].image,
        buttonText: modalDataPlans[currentPlan].buttonText,
    }

    console.log("modalData", modalData);
    console.log("activeSubscription", activeSubscription);

    const currentPlanName = modalDataPlans[currentPlan].name;
    const upgradeClickedRef = useRef(false);

    useEffect(() => {
        analyticsService?.event("paywall_shown", {
            category: "monetization",
            feature_name: currentPlan === PlanNames.free ? 'unlimited_messages' : 'pro_features',
            current_plan: currentPlanName,
        });

        return () => {
            if (!upgradeClickedRef.current) {
                analyticsService?.event("paywall_dismissed", {
                    category: "monetization",
                    feature_name: currentPlan === PlanNames.free ? 'unlimited_messages' : 'pro_features',
                    current_plan: currentPlanName,
                });
            }
        };
    }, []);


    return (
        <>

            <div className={classes.modal}>


                {isSubscriptionLoaded ? (
                    <div className={classes.body}>
                        <div className={classes.plan}>
                            <div className={classes.plan_name}>
                                {modifyPlanName(modalData.planName)}
                            </div>
                            <div className={classes.features}>
                                <div className={classes.feature}>
                                    <div className={classes.feature_name}>Messages</div>
                                    <div className={classes.feature_status}>{modalData.statuses[FeatureNames.messages]}</div>
                                </div>
                                <div className={classes.feature}>
                                    <div className={classes.feature_name}>Tasks</div>
                                    <div className={classes.feature_status}>{modalData.statuses[FeatureNames.tasks]}</div>
                                </div>
                                <div className={classes.feature}>
                                    <div className={classes.feature_name}>Campaigns</div>
                                    <div className={classes.feature_status}>{modalData.statuses[FeatureNames.campaigns]}</div>
                                </div>
                                <div className={classes.feature}>
                                    <div className={classes.feature_name}>Seats</div>
                                    <div className={classes.feature_status}>{activeSubscription?.seatCount ?? 1}</div>
                                </div>
                            </div>
                        </div>
                        <div className={classes.image} >
                            <img src={getRuntimeUrl(modalData.image)} alt="" />
                        </div>

                        <div className={classes.bottom}>
                            {modalData.planName.includes('Free') && (

                                <>
                                    <button
                                        className={classes.ctaButton}
                                        onClick={() => {
                                            upgradeClickedRef.current = true;
                                            analyticsService.event("click_unlock_all_features", {
                                                category: "upgrade",
                                                action: "click unlock all features",
                                                feature_name: 'unlimited_messages',
                                            });
                                            openPaymentSite(accessToken ?? '', true);
                                        }}
                                    >
                                        {modalData.buttonText}
                                    </button>
                                    <p style={{ margin: "13px 0 4px 0" }}>
                                        Only 3 messages at a time,<br /> Go premium to unlock unlimited messages.
                                    </p>
                                    <button className={classes.link} onClick={() => { upgradeClickedRef.current = true; openPaymentSite(accessToken ?? '', true); }}>
                                        See our pricing plans
                                    </button>
                                </>
                            )}

                            {(modalData.planName.includes('Basic') || modalData.planName.includes('Standard')) && (
                                <>
                                    <button
                                        className={classes.ctaButton}
                                        onClick={() => {
                                            upgradeClickedRef.current = true;
                                            analyticsService.event("click_unlock_all_features", {
                                                category: "upgrade",
                                                action: "click unlock all features",
                                                feature_name: 'pro_features',
                                            });
                                            openPaymentSite(accessToken ?? '', true);
                                        }}
                                    >
                                        {modalData.buttonText}
                                    </button>
                                    <p>
                                        Upgrade to Pro and unlock scheduling with your computer off,
                                        <br /> send campaigns without "Powered by Blueticks".
                                    </p>
                                    <button className={classes.link} onClick={() => { upgradeClickedRef.current = true; openPaymentSite(accessToken ?? '', true); }}>
                                        See our pricing plans
                                    </button>
                                </>
                            )}

                        </div>


                    </div>
                ) : (
                    <div className={classes.body} style={{ height: "500px" }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ p: 1 }}
                        >
                            <CircularProgress></CircularProgress>
                        </Stack>
                    </div>
                )}
            </div >

        </>
    );
}

export default StatusBox;
